import { DateTime } from 'luxon';
import * as dealHelpers from 'resources/deal/deal.helpers';
import { getDealTaskDueDate } from 'resources/milestone/dueDate.helpers';
import { getFullName } from 'resources/user/user.helpers';
import { TASK_ASSIGNEE_TYPES, TASK_TYPES, CLIENT_ASSIGNEE_TYPES, AGENT_ASSIGNEE_TYPES, AUTO_EMAIL_LABEL } from 'resources/task/task.constants';
import { MILESTONE_TYPES } from 'resources/milestone/milestone.constants';


export const callRefetchAllTasks = ({
  queryClient, userId, companyId, dealId, dealClientIds,
}) => {
  if (userId) {
    queryClient.invalidateQueries(['tasks.recentlyCompletedTasks', userId]); // Home and Tasks pages
    queryClient.invalidateQueries(['tasks.openTasks', userId]); // Home and Tasks pages
    queryClient.invalidateQueries(['deal.getByUserId', userId]); // Transactions list
    queryClient.invalidateQueries(['users.getRealtorClients', userId]); // Clients list
  }

  if (companyId) { // coordinator
    queryClient.invalidateQueries(['deal.getByCompanyId', companyId]); // Transactions list
    queryClient.invalidateQueries(['users.getCompanyClients', companyId]); // Clients list
  }

  if (dealId) {
    queryClient.invalidateQueries(['milestones.milestonesByDealId', dealId]); // Transaction page
    queryClient.invalidateQueries(['audits.auditsByDealId', dealId]); // Transaction page
    queryClient.invalidateQueries(['listedKits.getByDealId', dealId]); // Transaction page

    queryClient.invalidateQueries(['deal.getById', dealId]); // Transaction edit page

    queryClient.invalidateQueries(['documents.getByDealId', dealId]); // Transaction edit page

    queryClient.invalidateQueries(['users.getTransactionParties', dealId]); // Transaction parties
  }

  if (dealClientIds) {
    dealClientIds.forEach((clientId) => {
      queryClient.invalidateQueries(['deal.getByClientId', clientId]);
    });
  }
};

export const isDefault = (task) => task?.type === TASK_TYPES.TASK;
export const isEmailTemplate = (task) => task?.type === TASK_TYPES.EMAIL_TEMPLATE;
export const isAutoEmailTemplate = (task) => isEmailTemplate(task) && !!task.is_auto;
export const isDocumentRequest = (task) => task?.type === TASK_TYPES.DOCUMENT_REQUEST;

export const filterHiddenTasks = (tasks) => tasks?.filter((task) => !task.is_hidden_from_client);

export const isAssignedToCoordinator = (task) => task?.assignee_type === TASK_ASSIGNEE_TYPES.COORDINATOR;

export const filterOpenTasksAndExtendWithDueDate = (tasks) => (tasks
  .filter((task) => {
    const { deal, milestone } = task;
    const shouldSkipTask = task.is_skipped
      || (dealHelpers.hasStartedTransaction(deal) && milestone.type === MILESTONE_TYPES.PRE_CONTRACT)
      || (dealHelpers.isFinished(deal) && milestone.type !== MILESTONE_TYPES.AFTER_CLOSE)
      || (dealHelpers.isVoided(deal));

    return !shouldSkipTask;
  })
  .map((task) => ({
    ...task,
    dueDate: getDealTaskDueDate({ task, deal:task.deal }),
  }))
);

export const getTasksDueInNextWeekNumber = ({ startedTransactions, filteredOpenTasks, startedTransactionIds }) => {
  if (!startedTransactions || !filteredOpenTasks) return null;

  const tasksDueInNextDays = filteredOpenTasks
    .filter((task) => startedTransactionIds.includes(task.deal_id))
    .filter((task) => {
      if (!task.dueDate) return false;

      const now = DateTime.now();
      const weekLaterFromNow = now.plus({ week: 1 });

      return task.dueDate > now.toJSDate() && task.dueDate < weekLaterFromNow.toJSDate();
    });

  return tasksDueInNextDays.length;
};

// transform empty column name (from imported CSV file with tasks) into Excel-style name, 
// e.g.: Column A, Column B, ..., Column Z, Column AA, ...
export const calcTaskImportColumnName = (columnName, index) => {
  columnName = columnName.trim();
  if (columnName) return columnName;

  let dividend = index + 1; // Adjust for zero-based index
  while (dividend > 0) {
    let modulo = (dividend - 1) % 26;
    columnName = String.fromCharCode(65 + modulo) + columnName;
    dividend = Math.floor((dividend - 1) / 26);
  }

  return `Column ${columnName}`;
}

export const getAssigneeId = (deal, assigneeType) => {
  if (Object.values(CLIENT_ASSIGNEE_TYPES).includes(assigneeType))
    return deal.client_ids[0];

  if (Object.values(AGENT_ASSIGNEE_TYPES).includes(assigneeType))
    return deal.realtor_id;

  return deal.coordinator_id;
}

export const getAssigneeInfo = (task, companyUsers) => {
  if (isAutoEmailTemplate(task)) {
    return {
      assignedTo: AUTO_EMAIL_LABEL,
      assignee: null
    };
  }

  const assignee = companyUsers?.find((user) => user.id === task.assignee_id);
  return {
    assignedTo: assignee ? getFullName(assignee) : task.assignee_type,
    assignee
  };
};
